import {
  Dialog,
  DialogTitle as MuiDialogTitle,
  DialogContent,
  Grid,
  IconButton,
  Typography,
  withStyles
} from "@material-ui/core";
import { useState, useEffect } from "react";
import CloseIcon from "@material-ui/icons/Close";
import { LeftPanel, RightPanel, BackButton, Action } from './components';
import { withUserProfile } from "../../../../../../userProfile/userProfileContext";
import { withFeatureFlags } from "../../../../../../userProfile/featureFlagsContext";
import { withAppConfig } from "@appConfigContext";
import { compose } from "recompose";
import styles from "./styles";
import appConstants from "../../../../../../appConstants";
import { isPickupEditable } from "../../utils";
import Events from '../../../../../../utilities/EventEmitter';
import ViewStopImages from "../../DriverPopUp/ViewStopImages";
import { useLocation } from "react-router-dom";
import StopImagesDialog from '../../DriverPopUp/StopImagesDialog';
import { STOP_IMAGES } from '../../DriverPopUp/StopImagesDialog/getBlobImages';
import { getProNumbers } from "../../DriverPopUp/utils";

const anyGroupMemberPickupCompleted = (groupMemberPickups) =>
  (groupMemberPickups || []).some((p) => p.isCompleted);

const DialogTitle = withStyles(styles)((props) => {
  const {
    children,
    classes,
    onClose,
    onViewStopImages,
    showStopImagesForCompleted,
    ...other
  } = props;
  return (
    <>
      <MuiDialogTitle disableTypography classes={{ root: classes.dialogTitle }} {...other}>
        <Typography variant="h6" className={classes.title}>
          {children}
        </Typography>
        <div>
          {showStopImagesForCompleted ? (
            <ViewStopImages onClick={onViewStopImages} />
          ) : null}
          {onClose ? (
            <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          ) : null}
        </div>
      </MuiDialogTitle>
      <div className={classes.grayBar}></div>
    </>
  );
});

const PickupDialog = ({
  handleClose,
  classes,
  userData,
  pickInfoStateData,
  featureFlags,
  configJSON
}) => {
  const {
    pickupInfo,
    driverDetails,
    isPastPickup,
    terminalCode,
    isGroupPickupDialog,
    groupMemberPickups,
    groupBulkEditPickupIds
  } = pickInfoStateData;
  const showStopImagesForCompleted = isGroupPickupDialog
    ? anyGroupMemberPickupCompleted(groupMemberPickups)
    : pickupInfo.isCompleted;
  const isEditPickupEnabled = featureFlags.isFeatureEnabled(appConstants.features.EnablePickupEdit);
  const isGroupEditDisabled =
    isGroupPickupDialog &&
    (!configJSON?.enableBulkEditAndVoid ||
      (groupMemberPickups || []).some(
        (m) =>
          !isPickupEditable({
            isCompleted: m.isCompleted,
            isVoided: m.isVoided,
            isEnabled: isEditPickupEnabled
          })
      ));
  const isSingleEditDisabled =
    !isGroupPickupDialog &&
    !isPickupEditable({
      isCompleted: pickupInfo.isCompleted,
      isVoided: pickupInfo.isVoided,
      isEnabled: isEditPickupEnabled
    });
  const isEditPickupActionDisabled =
    isPastPickup ||
    isGroupEditDisabled ||
    isSingleEditDisabled ||
    (isGroupPickupDialog && !groupBulkEditPickupIds?.length);
  const { theme } = userData.userProfile || {};
  const [isResized, setIsResized] = useState(false);
  const location = useLocation();
  const [viewStopImages, setViewStopImages] = useState(null);
  
  const onViewStopImages = () => {
    setViewStopImages({
      terminalCode: terminalCode || userData.userProfile.terminal.code, //passing terminal code from props but if it is not there in that case we are using default one.
      routeId: pickupInfo.routeId,
      stopId: driverDetails?.stops?.[0]?.stopId,
      isStopCompleted: pickInfoStateData.isGroupPickupDialog
        ? anyGroupMemberPickupCompleted(pickInfoStateData.groupMemberPickups)
        : pickupInfo.isCompleted,
      proNumbers: getProNumbers(driverDetails?.stops?.[0]?.shipments || [])
    });
  };

  const onHideStopImages = () => {
    setViewStopImages(null);
  };

  useEffect(() => {
    if (!isResized && location?.state?.isResized) {
      setIsResized(true);
    }
    const resizeDialog = ({ isResized }) => {
      setIsResized(isResized);
    };
    Events.on('ResizePickupInfo', resizeDialog);
    return () => {
      Events.off('ResizePickupInfo', resizeDialog);
    };
  }, []);

  const resizePickInfoClass = isResized ? classes.resizePickInfo : null;
  return (
    <Dialog
      open
      onClose={handleClose}
      aria-labelledby="dialog-title"
      classes={{ paper: classes.dialog, root: classes.root }}
      className={`${classes.dialogWrapper} ${theme}`}>
      <DialogTitle
        id="dialog-title"
        onClose={handleClose}
        onViewStopImages={onViewStopImages}
        showStopImagesForCompleted={showStopImagesForCompleted}>
        <>
          <BackButton onClick={handleClose} />
          {isGroupPickupDialog ? 'Group Information' : 'Pickup Information'}
        </>
      </DialogTitle>
      <DialogContent
        classes={{ root: classes.dialogContent }}
        className={`${resizePickInfoClass} customScroll`}>
        <Grid container spacing={24}>
          <Grid item xs={3}>
            <LeftPanel pickupInfo={pickupInfo} hidePickupNumber={Boolean(isGroupPickupDialog)} />
          </Grid>
          <Grid item xs={9}>
            <RightPanel pickInfoStateData={pickInfoStateData} />
          </Grid>
        </Grid>
      </DialogContent>
      <Action
        pickupInfo={pickupInfo}
        pickInfoStateData={pickInfoStateData}
        isEditPickupActionDisabled={isEditPickupActionDisabled}
        handleClose={handleClose}
        resizePickInfoClass={resizePickInfoClass}
      />
      {viewStopImages ? (
        <StopImagesDialog
          showImages={Object.keys(STOP_IMAGES)[0]}
          {...viewStopImages}
          stops={driverDetails?.stops ?? []}
          close={onHideStopImages}
          apiName={pickInfoStateData.proImageApiName || 'GetImages'}
          dialogRootClass={classes.dialogRootClass}
        />
      ) : null}
    </Dialog>
  );
};

export default compose(withUserProfile, withFeatureFlags, withAppConfig, withStyles(styles))(PickupDialog);
